import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef } from "react";
import { FieldValues, useForm, UseFormSetValue } from "react-hook-form";

import { ICatchARideAvailability, Log } from "@ryde/shared";

import { emptyCatchARideAvailability } from "./catch-a-ride-availability.models";
import {
  hasAnyCatchARideCondition,
  normalizeCatchARideAvailability,
} from "./catch-a-ride-availability.utils";
import { useCatchARideAvailabilityItemResolver } from "./use-catch-a-ride-availability-item-validation";

export const useCatchARideAvailabilityItemEdit = ({
  fieldName,
  value,
  setValueOuter,
  onCancelOuter,
}: {
  fieldName: string;
  value?: Partial<ICatchARideAvailability>;
  setValueOuter?: UseFormSetValue<FieldValues>;
  onCancelOuter?: () => void;
}) => {
  const { catchARideAvailabilityInnerFormValidationSchema } =
    useCatchARideAvailabilityItemResolver();
  const methods = useForm<Partial<ICatchARideAvailability>>({
    defaultValues: { ...emptyCatchARideAvailability, ...value },
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(catchARideAvailabilityInnerFormValidationSchema),
  });
  const {
    getValues,
    watch,
    trigger,
    formState: { isValid, errors },
  } = methods;
  const editorRef = useRef<HTMLDivElement | null>(null);
  const hasAnyCondition = hasAnyCatchARideCondition(watch());

  // The button that opened the editor is gone once it mounts; land focus on the first control instead of <body>.
  useEffect(() => {
    editorRef.current?.querySelector<HTMLElement>("input, button")?.focus();
  }, []);

  const onSaveClick = async (): Promise<void> => {
    const validated = await trigger();

    if (!validated || !hasAnyCondition) {
      Log.debug("Catch a ride availability item validation errors:", errors);
      return;
    }

    const valueSnapshot = normalizeCatchARideAvailability(getValues());

    Log.debug(
      `Set value for catch a ride availability item target field: ${fieldName}`,
      valueSnapshot,
    );
    setValueOuter?.(fieldName, valueSnapshot, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancelOuter?.();
  };

  return {
    methods,
    editorRef,
    hasAnyCondition,
    canSave: isValid && hasAnyCondition,
    onSaveClick,
  };
};
